import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Gift, Send, MapPin, CheckCircle, XCircle, ArrowLeft, Clock, MessageCircle, User, Siren, Phone, ZoomIn, X, Key, Car, Dog, Baby, Smartphone, Package, Heart, Building2, ExternalLink } from 'lucide-react';
import { API, useAuth } from '../App';
import { t, allCategories, getDefaultTranslations } from '../i18n';
import { LocationChip } from '../components/LocationMapModal';
import TagoraSeal from '../components/TagoraSeal';
import { toast } from 'sonner';

// French category labels - always shown on scan regardless of user language
const FR_CATEGORIES = getDefaultTranslations('fr').categories || {};


// Organization branding banner shown at top when PingMe is linked to an org
const OrgBrandingBanner = ({ org }) => {
  if (!org) return null;
  const primary = org.primary_color || '#FF6B35';
  const resolveUrl = (u) => (u && u.startsWith('/api') ? `${API.replace('/api', '')}${u}` : u);
  return (
    <div
      className="mb-6 rounded-2xl border-2 border-charcoal overflow-hidden shadow-brutal"
      data-testid="org-branding-banner"
      style={{ backgroundColor: primary + '10' }}
    >
      {org.cover_image_url && (
        <div className="w-full aspect-[16/6] bg-charcoal/5 overflow-hidden">
          <img src={resolveUrl(org.cover_image_url)} alt={org.display_name || org.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-2">
          {org.logo_url ? (
            <img src={resolveUrl(org.logo_url)} alt="" className="w-12 h-12 rounded-xl border-2 border-charcoal object-cover bg-white" />
          ) : (
            <div className="w-12 h-12 rounded-xl border-2 border-charcoal flex items-center justify-center" style={{ backgroundColor: primary }}>
              <Building2 className="w-6 h-6 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-charcoal/60">PingMe officiel</p>
            <h2 className="font-heading font-bold text-lg text-charcoal truncate">{org.display_name || org.name}</h2>
          </div>
        </div>
        {org.tagline && (
          <p className="text-sm text-charcoal/70 italic mb-2" data-testid="org-tagline">"{org.tagline}"</p>
        )}
        {org.message && (
          <p className="text-sm text-charcoal leading-relaxed" data-testid="org-message">{org.message}</p>
        )}
        {org.website_url && (
          <a
            href={org.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-charcoal underline hover:no-underline"
            data-testid="org-website-link"
          >
            <ExternalLink className="w-3 h-3" /> Visiter le site
          </a>
        )}
      </div>
    </div>
  );
};


const PingPage = () => {
  const { pingmeId } = useParams();
  const { user } = useAuth();
  const [pingme, setPingme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [senderName, setSenderName] = useState('');
  const [location, setLocation] = useState('');
  const [sending, setSending] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Claim state (for unassigned PingMes) - empty so user must choose
  const [claimName, setClaimName] = useState('');
  const [claimCategory, setClaimCategory] = useState('');
  const [claiming, setClaiming] = useState(false);
  
  // Conversation state
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [senderToken, setSenderToken] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [senderMessageSent, setSenderMessageSent] = useState(false);
  const [canSenderReply, setCanSenderReply] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [sharedPhone, setSharedPhone] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);

  useEffect(() => {
    fetchPingme();
    // Check for existing conversation token in localStorage
    const storedToken = localStorage.getItem(`conv_token_${pingmeId}`);
    const storedConvId = localStorage.getItem(`conv_id_${pingmeId}`);
    if (storedToken && storedConvId) {
      setSenderToken(storedToken);
      fetchConversation(storedConvId, storedToken);
    }
  }, [pingmeId]);

  // Countdown timer
  useEffect(() => {
    if (!remainingTime || remainingTime <= 0) return;
    
    const interval = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [remainingTime]);

  const fetchPingme = async () => {
    try {
      const response = await axios.get(`${API}/ping/${pingmeId}/public`);
      setPingme(response.data);
      // If org has custom categories, preselect the first one for claim form
      const orgCats = response.data?.organization?.categories;
      if (Array.isArray(orgCats) && orgCats.length > 0) {
        setClaimCategory(orgCats[0].key);
      }
      // Auto-send geolocation on scan
      sendScanGeolocation();
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Ce PingMe n\'existe pas');
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'Ce PingMe est actuellement désactivé');
      } else {
        setError(t('actions.search'));
      }
    } finally {
      setLoading(false);
    }
  };

  const sendScanGeolocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        try {
          await axios.post(`${API}/ping/${pingmeId}/scan`, { latitude, longitude });
        } catch { /* silent - scan tracking is best-effort */ }
      },
      () => { /* user denied - that's fine */ },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  const fetchConversation = async (convId, token) => {
    try {
      const response = await axios.get(`${API}/conversations/${convId}?sender_token=${token}`);
      setConversation(response.data);
      setMessages(response.data.messages || []);
      setRemainingTime(response.data.remaining_seconds);
      setIsBlocked(response.data.is_blocked || false);
      setSharedPhone(response.data.shared_phone || null);
      const msgs = response.data.messages || [];
      const senderMsgs = msgs.filter(m => !m.is_from_owner);
      setSenderMessageSent(senderMsgs.length >= 1);
      // Sender can reply if last message is from owner
      const lastMsg = msgs.length > 0 ? msgs[msgs.length - 1] : null;
      setCanSenderReply(!lastMsg || lastMsg.is_from_owner);
    } catch (err) {
      localStorage.removeItem(`conv_token_${pingmeId}`);
      localStorage.removeItem(`conv_id_${pingmeId}`);
      setSenderToken(null);
      setConversation(null);
    }
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        setGettingLocation(false);
      },
      () => {
        toast.error('GPS error');
        setGettingLocation(false);
      }
    );
  };

  const startConversation = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    try {
      // Create conversation - send credentials if logged in so the backend can track finder
      const convResponse = await axios.post(`${API}/conversations`, {
        pingme_id: pingmeId,
        sender_name: senderName || (user ? user.name : 'Anonymous')
      }, { withCredentials: true });

      const { conversation_id, sender_token, remaining_seconds } = convResponse.data;
      
      // Store token in localStorage
      localStorage.setItem(`conv_token_${pingmeId}`, sender_token);
      localStorage.setItem(`conv_id_${pingmeId}`, conversation_id);
      setSenderToken(sender_token);
      setRemainingTime(remaining_seconds || 24 * 60 * 60);

      // Send first message
      const msgResponse = await axios.post(`${API}/messages`, {
        pingme_id: pingmeId,
        content: message,
        sender_name: senderName || (user ? user.name : 'Anonymous'),
        location: location || null,
        conversation_id
      }, { withCredentials: true });

      setConversation({
        conversation_id,
        sender_name: senderName || (user ? user.name : 'Anonymous'),
        remaining_seconds: remaining_seconds || 24 * 60 * 60
      });
      setMessages([msgResponse.data]);
      setMessage('');
      setSenderMessageSent(true);
      setCanSenderReply(false); // Just sent first message, wait for owner
      toast.success(t('messages.messageSent'));
    } catch (err) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!message.trim() || !conversation) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        pingme_id: pingmeId,
        content: message,
        sender_name: conversation.sender_name,
        conversation_id: conversation.conversation_id
      }, { withCredentials: true });

      const newMessages = [...messages, response.data];
      setMessages(newMessages);
      setMessage('');
      setSenderMessageSent(true);
      setCanSenderReply(false); // Just sent, wait for owner reply
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const refreshMessages = useCallback(async () => {
    if (!conversation || !senderToken) return;
    try {
      const response = await axios.get(
        `${API}/conversations/${conversation.conversation_id}?sender_token=${senderToken}`
      );
      const msgs = response.data.messages || [];
      setMessages(msgs);
      setRemainingTime(response.data.remaining_seconds);
      setIsBlocked(response.data.is_blocked || false);
      setSharedPhone(response.data.shared_phone || null);
      const senderMsgs = msgs.filter(m => !m.is_from_owner);
      setSenderMessageSent(senderMsgs.length >= 1);
      const lastMsg = msgs.length > 0 ? msgs[msgs.length - 1] : null;
      setCanSenderReply(!lastMsg || lastMsg.is_from_owner);
    } catch (err) {
      console.error('Error refreshing messages:', err);
    }
  }, [conversation, senderToken]);

  // Auto-refresh messages every 10 seconds
  useEffect(() => {
    if (!conversation || !senderToken) return;
    const interval = setInterval(refreshMessages, 10000);
    return () => clearInterval(interval);
  }, [conversation, senderToken, refreshMessages]);

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="card-brutal max-w-md w-full text-center">
          <div className="bg-danger w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-bold text-charcoal font-heading mb-4">{error}</h1>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  // Unassigned PingMe - Claim flow
  if (pingme?.unassigned) {
    const handleClaim = async (e) => {
      e.preventDefault();
      if (!claimName.trim()) return;
      if (!user) {
        toast.error(t('nav.login'));
        return;
      }
      setClaiming(true);
      try {
        const res = await axios.post(`${API}/pingmes/${pingmeId}/claim`, {
          name: claimName,
          category: claimCategory
        }, { withCredentials: true });
        setPingme({ ...res, ...pingme, unassigned: false, name: claimName, category: claimCategory });
        toast.success(t('ping.claimBtn') + ' !');
        window.location.href = '/dashboard';
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur lors de l\'enrolement');
      } finally { setClaiming(false); }
    };

    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-8">
        <div className="max-w-md w-full" data-testid="claim-pingme-page">
          <OrgBrandingBanner org={pingme.organization} />
          <div className="card-brutal">
          <div className="text-center mb-6">
            <div className="bg-primary w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-charcoal" />
            </div>
            <h1 className="text-2xl font-bold text-charcoal font-heading mb-2">Nouveau PingMe !</h1>
            <p className="text-charcoal/70">
              {pingme.organization
                ? `Ce PingMe est fourni par ${pingme.organization.display_name || pingme.organization.name}. Enrolez-le pour le rattacher a votre compte.`
                : "Ce PingMe n'est pas encore assigne. Enrolez-le pour le rattacher a votre compte."}
            </p>
            {pingme.label_text && (
              <p className="text-sm text-danger font-bold mt-2">"{pingme.label_text}"</p>
            )}
          </div>
          
          {!user ? (
            <div className="text-center">
              <p className="text-charcoal/70 mb-4">Connectez-vous pour enroler ce PingMe</p>
              <div className="flex flex-wrap justify-center gap-2">
                <Link
                  to="/login"
                  state={{ from: { pathname: `/ping/${pingmeId}` } }}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="claim-login-link"
                >
                  Connexion
                </Link>
                <Link
                  to="/register"
                  state={{ from: { pathname: `/ping/${pingmeId}` } }}
                  className="btn-outline inline-flex items-center gap-2"
                  data-testid="claim-register-link"
                >
                  Creer un compte
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleClaim} className="space-y-4">
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Nom de l'objet *</label>
                <input
                  className="input-brutal w-full"
                  placeholder={t('ping.yourMessage')}
                  value={claimName}
                  onChange={(e) => setClaimName(e.target.value)}
                  required
                  data-testid="claim-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Categorie</label>
                <select
                  className="input-brutal w-full"
                  value={claimCategory}
                  onChange={(e) => setClaimCategory(e.target.value)}
                  data-testid="claim-category-select"
                  required
                >
                  <option value="" disabled>Choisir une categorie...</option>
                  {(pingme.organization?.categories && pingme.organization.categories.length > 0)
                    ? pingme.organization.categories.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))
                    : [
                        ['keys', 'Cles'],
                        ['phone', 'Telephone & electronique'],
                        ['bag', 'Sac & bagage'],
                        ['clothing', 'Vetements'],
                        ['child', 'Objets enfants & doudou'],
                        ['pet', 'Animaux'],
                        ['bike', 'Velo & mobilite'],
                        ['car', 'Voiture'],
                        ['valuables', 'Objets de valeur'],
                        ['accessories', 'Accessoires du quotidien'],
                        ['home', 'Maison & loisirs'],
                      ].map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))
                  }
                </select>
              </div>
              <button
                type="submit"
                disabled={claiming}
                className="w-full btn-primary disabled:opacity-50"
                data-testid="claim-submit-btn"
              >
                {claiming ? 'Enrolement...' : 'Enroler ce PingMe'}
              </button>
            </form>
          )}
          </div>
        </div>
      </div>
    );
  }

  // Conversation view with messages
  if (conversation && senderToken) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b-2 border-charcoal px-6 py-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/" className="flex items-center gap-2">
                  <div className="bg-primary w-10 h-10 rounded-full border-2 border-charcoal flex items-center justify-center">
                    <span className="font-heading font-bold text-charcoal">T</span>
                  </div>
                </Link>
                <div>
                  <p className="font-bold text-charcoal">{pingme.name}</p>
                  <p className="text-xs text-charcoal/60">{conversation.sender_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-accent border-2 border-charcoal rounded-full px-3 py-1">
                <Clock className="w-4 h-4 text-charcoal" />
                <span className="text-sm font-mono font-bold text-charcoal">{formatTime(remainingTime)}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Expiry warning */}
        {remainingTime && remainingTime < 3600 && (
          <div className="bg-danger/10 border-b-2 border-danger px-6 py-2">
            <p className="text-center text-sm text-danger font-bold">
              Cette conversation expire bientôt!
            </p>
          </div>
        )}

        {/* Messages */}
        <main className="flex-1 px-6 py-4 overflow-y-auto">
          <div className="max-w-lg mx-auto space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={msg.message_id || idx}
                className={`flex ${msg.is_from_owner ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[80%] ${
                    msg.is_from_owner
                      ? 'bg-white text-charcoal border-2 border-charcoal rounded-2xl rounded-bl-none'
                      : 'bg-primary text-charcoal border-2 border-charcoal rounded-2xl rounded-br-none'
                  } p-4`}
                >
                  <p className="text-sm">{msg.content}</p>
                  {msg.location && (
                    <p className="text-xs opacity-70 mt-2 flex items-center gap-1">
                      <MapPin className="w-3 h-3 shrink-0" />
                      <LocationChip value={msg.location} />
                    </p>
                  )}
                  <p className="text-xs opacity-50 mt-2">{formatDate(msg.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </main>

        {/* Reply form */}
        <div className="sticky bottom-0 bg-background border-t-2 border-charcoal px-6 py-4">
          {isBlocked ? (
            <div className="max-w-lg mx-auto text-center">
              <p className="text-danger font-bold text-sm">Cette conversation a ete bloquee par le proprietaire.</p>
            </div>
          ) : canSenderReply ? (
            <div className="max-w-lg mx-auto">
              <form onSubmit={sendReply} className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="reply-input"
                  className="input-brutal flex-1"
                  placeholder={t('messages.replyPlaceholder')}
                />
                <button
                  type="submit"
                  disabled={sending || !message.trim() || remainingTime <= 0}
                  data-testid="send-reply-btn"
                  className="btn-primary px-4 disabled:opacity-50"
                >
                  <Send className="w-5 h-5" strokeWidth={2.5} />
                </button>
              </form>
              {user && conversation && (
                <div className="text-center mt-2">
                  <Link
                    to={`/messages?finder=${conversation.conversation_id}`}
                    className="inline-flex items-center gap-2 text-primary font-bold text-xs hover:underline"
                    data-testid="follow-from-account-link"
                  >
                    <MessageCircle className="w-3 h-3" />
                    Suivre depuis mon compte
                  </Link>
                </div>
              )}
            </div>
          ) : senderMessageSent ? (
            <div className="max-w-lg mx-auto text-center">
              <p className="text-charcoal/70 text-sm font-medium">En attente de la reponse du proprietaire...</p>
              {sharedPhone && (
                <div className="mt-3 flex items-center justify-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-300 text-sm font-bold">
                  <Phone className="w-4 h-4" />
                  Numero du proprietaire : {sharedPhone}
                </div>
              )}
              {user && conversation && (
                <Link
                  to={`/messages?finder=${conversation.conversation_id}`}
                  className="mt-3 inline-flex items-center gap-2 text-primary font-bold text-sm hover:underline"
                  data-testid="follow-from-account-link"
                >
                  <MessageCircle className="w-4 h-4" />
                  Suivre cette conversation depuis mon compte
                </Link>
              )}
              {!user && (
                <p className="text-xs text-charcoal/50 mt-3">
                  <Link to="/login" className="text-primary font-bold hover:underline">Connectez-vous</Link> pour suivre cette conversation depuis votre compte
                </p>
              )}
            </div>
          ) : (
          <form onSubmit={sendReply} className="max-w-lg mx-auto flex gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="reply-input"
              className="input-brutal flex-1"
              placeholder={t('ping.yourMessage')}
            />
            <button
              type="submit"
              disabled={sending || !message.trim() || remainingTime <= 0}
              data-testid="send-reply-btn"
              className="btn-primary px-4 disabled:opacity-50"
            >
              <Send className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </form>
          )}
          {remainingTime <= 0 && (
            <p className="text-center text-sm text-danger mt-2">Cette conversation a expire</p>
          )}
        </div>
      </div>
    );
  }

  // Initial contact form
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b-2 border-charcoal px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="bg-primary w-10 h-10 rounded-full border-2 border-charcoal flex items-center justify-center">
              <span className="font-heading font-bold text-charcoal">T</span>
            </div>
            <span className="font-heading font-bold text-lg text-charcoal">Tagora</span>
          </Link>
        </div>
      </header>

      <main className="px-6 py-8">
        <div className="max-w-lg mx-auto">
          {/* Organization branding (shown before any PingMe info) */}
          <OrgBrandingBanner org={pingme.organization} />

          {/* Lost Mode Alert */}
          {pingme.is_lost && (
            <div className={`${pingme.urgent_lost ? 'bg-danger' : 'bg-danger/90'} text-white border-2 border-charcoal rounded-2xl p-6 mb-6 shadow-brutal`}>
              <div className="flex items-start gap-4">
                {pingme.urgent_lost ? <Siren className="w-8 h-8 flex-shrink-0 animate-pulse" strokeWidth={2.5} /> : <AlertTriangle className="w-8 h-8 flex-shrink-0" strokeWidth={2.5} />}
                <div>
                  <h2 className="font-bold text-lg mb-1">{pingme.urgent_lost ? 'RECHERCHE URGENTE' : 'Objet signale perdu!'}</h2>
                  <p className="text-white/90">Le proprietaire recherche activement cet objet.</p>
                  {pingme.urgent_details && (
                    <div className="mt-3 space-y-1 text-sm text-white/80">
                      {pingme.urgent_details.object_type && <p>Type: <span className="font-bold text-white">{pingme.urgent_details.object_type}</span></p>}
                      {pingme.urgent_details.last_location && (
                        <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Derniere localisation: <span className="font-bold text-white">{pingme.urgent_details.last_location}</span></p>
                      )}
                      {pingme.urgent_details.description && <p className="italic">"{pingme.urgent_details.description}"</p>}
                    </div>
                  )}
                  {pingme.reward_amount && (
                    <div className="flex items-center gap-2 mt-3 bg-white/20 rounded-full px-4 py-2 w-fit">
                      <Gift className="w-5 h-5" />
                      <span className="font-bold">Recompense: {pingme.reward_amount} EUR</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Object Info Card */}
          <div className="card-brutal mb-6">
            <div className="flex items-start gap-4">
              {pingme.photo_url && (
                <img 
                  src={pingme.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${pingme.photo_url}` : pingme.photo_url} 
                  alt={pingme.name}
                  className="w-20 h-20 rounded-xl border-2 border-charcoal object-cover cursor-zoom-in hover:opacity-80 transition-opacity"
                  onClick={() => setZoomedImage(pingme.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${pingme.photo_url}` : pingme.photo_url)}
                />
              )}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-charcoal font-heading mb-2">
                  {pingme.name}
                </h1>
                <div className="inline-flex items-center gap-2 bg-secondary text-charcoal text-sm font-bold px-3 py-1 rounded-full border-2 border-charcoal">
                  {(() => {
                    const orgCat = pingme.organization?.categories?.find((c) => c.key === pingme.category);
                    if (orgCat) return orgCat.label;
                    return FR_CATEGORIES[pingme.category] || pingme.category;
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Preset Message */}
          {pingme.preset_message && (
            <div className="card-brutal mb-6 bg-accent/10 border-accent" data-testid="preset-message-card">
              <div className="flex items-start gap-3">
                <div className="bg-accent w-10 h-10 rounded-full border-2 border-charcoal flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-charcoal" />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/60 mb-1">Message du proprietaire</p>
                  <p className="text-charcoal font-medium">{pingme.preset_message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Contact Form - only if mode is not "only" */}
          {(!pingme.preset_message || pingme.preset_message_mode !== 'only') && (
          <div className="card-brutal">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-accent w-10 h-10 rounded-full border-2 border-charcoal flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-charcoal" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-charcoal font-heading">
                  Démarrer une conversation
                </h2>
                <p className="text-sm text-charcoal/70">Expire dans 24h</p>
              </div>
            </div>
            
            <p className="text-charcoal/70 mb-6 text-sm">
              Votre message sera envoyé de façon anonyme. Le propriétaire pourra vous répondre dans cette conversation pendant 24 heures.
            </p>

            <form onSubmit={startConversation} className="space-y-5">
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                  Votre nom (optionnel)
                </label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  data-testid="sender-name-input"
                  className="input-brutal w-full"
                  placeholder={t('ping.yourName')}
                />
              </div>

              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                  Votre message *
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="message-input"
                  className="input-brutal w-full h-32 resize-none"
                  placeholder={t('ping.yourMessage')}
                  required
                />
              </div>

              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                  Localisation (optionnel)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    data-testid="location-input"
                    className="input-brutal flex-1"
                    placeholder={t('ping.yourMessage')}
                  />
                  <button
                    type="button"
                    onClick={getLocation}
                    disabled={gettingLocation}
                    data-testid="get-location-btn"
                    className="btn-secondary px-4 disabled:opacity-50"
                  >
                    <MapPin className="w-5 h-5" strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={sending || !message.trim()}
                data-testid="send-message-btn"
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Send className="w-5 h-5" strokeWidth={2.5} />
                {sending ? 'Envoi...' : 'Démarrer la conversation'}
              </button>
            </form>

            <div className="mt-4 p-3 bg-accent/20 rounded-xl border-2 border-charcoal/20">
              <p className="text-xs text-charcoal/70 text-center">
                <Clock className="w-4 h-4 inline mr-1" />
                Cette conversation expirera automatiquement dans 24 heures
              </p>
            </div>
          </div>
          )}

          {/* Tagora ID seal + footer */}
          <div className="mt-8 flex flex-col items-center gap-3">
            <Link to={`/verify/${pingmeId}`} title="Verifier ce code" className="hover:opacity-80 transition-opacity" data-testid="ping-tagora-seal">
              <TagoraSeal tagoraId={pingmeId} verified />
            </Link>
            <p className="text-center text-xs text-charcoal/50">
              Code <Link to={`/verify/${pingmeId}`} className="font-bold text-charcoal hover:underline">verifiable</Link> ·{' '}
              Standard <Link to="/label" className="font-bold text-primary hover:underline">Tagora ID</Link>
            </p>
            <p className="text-center text-sm text-charcoal/50">
              Propulsé par{' '}
              <Link to="/" className="text-primary font-bold hover:underline">
                Tagora
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/80 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setZoomedImage(null)}
          data-testid="image-zoom-modal"
        >
          <button onClick={() => setZoomedImage(null)} className="absolute top-4 right-4 p-2 bg-white rounded-full border-2 border-charcoal shadow-brutal z-10">
            <X className="w-6 h-6 text-charcoal" />
          </button>
          <img
            src={zoomedImage}
            alt="Zoom"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-2xl border-4 border-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PingPage;
